import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { advanceDiscovery, discoveryStation, DISCOVERY_TOTALS, initialDiscovery, MUSIC_PHRASES, type DiscoveryGameId } from "../shared/discovery-game.ts";
import { DiscoveryWorld } from "./DiscoveryWorld.tsx";
import { GentleGameArena, GentleGameIntro, GentleGameResult } from "./GentleGameFrame.tsx";
import { useGameAudio, type GameSoundCue } from "./gameAudio.ts";
import { useLocale } from "./i18n.tsx";
import { PlusGameGate } from "./PlusGameGate.tsx";
import type { Session } from "./Shell.tsx";
import { useGentleGame } from "./useGentleGame.ts";

export const DISCOVERY_COPY = {
  "zh-CN": {
    "forest-post": { title: "森林邮差", tag: "认门牌，送心意", lead: "背上邮包出发吧。看清信封上的图案，轻轻转头，把信送到相同门牌的小屋。投递后回到中央邮局，盖好邮戳再领取下一封。十二封心意，等你送达。", seek: "把信送到相同图案的小屋", returning: "回到邮局，盖上邮戳", result: "十二封心意，都送到了", unit: "封信" },
    "reef-lens": { title: "深海留影", tag: "稳住镜头，收藏海洋", lead: "带上相机，拜访珊瑚礁的小居民。轻轻移动取景框，把它们留在镜头中央；对焦完成后回到中央相机站，冲印一张照片。慢一点，海底的朋友会等你。", seek: "把小居民放进取景框，稳稳停留", returning: "对焦好了，回到相机站冲印", result: "你的海底相册，装满了", unit: "张照片" },
    "moon-harp": { title: "月下琴庭", tag: "听一小段，奏一小段", lead: "月光会先弹出三个音。看着乐谱，轻轻转头拨响同样的琴弦，每个音之后回到中央。六段旋律会铺成一首小夜曲；静音也能跟着亮起的数字弹奏。", seek: "按乐谱，拨响下一个音", returning: "回到中央，让琴音慢慢落下", result: "一首月光小夜曲，为你响起", unit: "个音" },
  },
  en: {
    "forest-post": { title: "Forest Post", tag: "A little mail, a little joy", lead: "Carry a bag of letters into the woods. Match the symbol on each envelope to a woodland home with a gentle turn. Return to the central post office to stamp your journal and collect the next letter. Twelve little messages are waiting.", seek: "Deliver the letter to the matching symbol", returning: "Return to the post office for your stamp", result: "Twelve little messages, delivered", unit: "letters" },
    "reef-lens": { title: "Reef Portraits", tag: "Find your frame beneath the waves", lead: "Meet the residents of a quiet coral reef. Gently move the viewfinder to frame each creature and hold it steady. Once focused, return to the central camera station to develop a photograph. Your ocean friends will wait.", seek: "Frame your reef friend and hold steady", returning: "In focus. Return to the camera to develop", result: "An album full of ocean friends", unit: "photos" },
    "moon-harp": { title: "Moonlit Harp", tag: "A little listening, a little music", lead: "The moon plays three notes first. Follow the score and turn gently to pluck the same strings, returning to centre between notes. Six phrases become a little nocturne. The lit numbers guide you even with sound off.", seek: "Follow the score. Play the next note", returning: "Return to centre and let the note settle", result: "Your moonlit nocturne is complete", unit: "notes" },
  },
} as const;

function DiscoveryGame({ game, session }: { game: DiscoveryGameId; session: Session | null }) {
  const { locale } = useLocale();
  const zh = locale === "zh-CN";
  const text = DISCOVERY_COPY[locale][game];
  const runtime = useGentleGame(game, session, { calibrateComfort: true });
  const audio = useGameAudio(game);
  const stateRef = useRef(initialDiscovery(game));
  const [state, setState] = useState(stateRef.current);
  const pointRef = useRef(runtime.point);
  pointRef.current = runtime.point;
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  const paintRef = useRef(0);
  const togglePause = useCallback(() => {
    pausedRef.current = !pausedRef.current;
    setPaused(pausedRef.current);
  }, []);

  useEffect(() => {
    if (runtime.stage !== "intro") return;
    stateRef.current = initialDiscovery(game);
    setState(stateRef.current);
    pausedRef.current = false;
    setPaused(false);
    audio.stop();
  }, [game, runtime.stage, audio.stop]);

  useEffect(() => {
    if (paused) audio.stop();
  }, [paused, audio.stop]);

  useEffect(() => {
    if (runtime.stage !== "playing") return;
    const pause = () => { pausedRef.current = true; setPaused(true); };
    const visibility = () => { if (document.hidden) pause(); };
    const key = (event: KeyboardEvent) => { if (event.key === "Escape") togglePause(); };
    window.addEventListener("blur", pause);
    window.addEventListener("keydown", key);
    document.addEventListener("visibilitychange", visibility);
    let frame = 0;
    let previous = performance.now();
    const tick = (now: number) => {
      const delta = now - previous;
      previous = now;
      const current = stateRef.current;
      const next = pausedRef.current ? current : advanceDiscovery(game, current, delta, pointRef.current);
      if (next !== current) {
        if (current.phase === "listen") {
          const note = Math.floor((next.phaseMs - 600) / 1000);
          if (next.phase === "listen" && note >= 0 && note < 3 && note !== Math.floor((current.phaseMs - 600) / 1000)) {
            audio.play(`note-${MUSIC_PHRASES[Math.floor(current.done / 3)][note]}` as GameSoundCue);
          }
        }
        if (next.phase === "return" && current.phase === "seek") audio.play(game === "moon-harp" ? `note-${discoveryStation(game, current)}` as GameSoundCue : "arrive");
        if (next.done > current.done) {
          if (next.phase === "complete") audio.play("complete");
          else if (next.phase === "rest") audio.play("rest");
          else if (game !== "moon-harp" || next.done % 3 === 0) audio.play("center");
        }
        stateRef.current = next;
        if (next.phase !== current.phase || next.done !== current.done || now - paintRef.current >= 60) {
          paintRef.current = now;
          setState(next);
        }
      }
      if (next.phase === "complete") { runtime.complete(); return; }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("blur", pause);
      window.removeEventListener("keydown", key);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [audio.play, game, runtime.complete, runtime.stage, togglePause]);

  const controlsRuntime = useMemo(() => ({ ...runtime,
    startManual: () => { audio.unlock(); runtime.startManual(); },
    startCamera: async () => { audio.unlock(); await runtime.startCamera(); },
    reset: () => { audio.stop(); runtime.reset(); },
  }), [runtime, audio.unlock, audio.stop]);
  if (runtime.stage === "intro") return <GentleGameIntro runtime={controlsRuntime} tone={game} eyebrow={`Plus · ${text.tag}`} title={text.title} lead={text.lead} previewLabel={text.title} preview={<DiscoveryWorld game={game} state={initialDiscovery(game)} />} />;
  if (runtime.stage === "complete") return <GentleGameResult runtime={controlsRuntime} tone={game} eyebrow={text.title} title={text.result} body={zh ? "回到自然位置，眨眨眼，放松肩膀。今天的这段小小冒险，圆满收好。" : "Return to a natural centre, blink and relax your shoulders. Keep this little adventure with you."} art={<DiscoveryWorld game={game} state={state} />} />;
  const title = paused ? (zh ? "已暂停，准备好再继续" : "Paused. Continue when you are ready")
    : state.phase === "rest" ? (zh ? "回到中央，休息一会儿" : "Return to centre and rest")
      : state.phase === "listen" ? (zh ? "先听一小段，看亮起的乐谱" : "Listen first. Watch the lit score")
        : state.phase === "return" ? text.returning : text.seek;
  const detail = state.phase === "rest" ? `${Math.ceil((15000 - state.phaseMs) / 1000)} ${zh ? "秒 · 放松肩膀" : "seconds · relax your shoulders"}`
    : `${state.done} / ${DISCOVERY_TOTALS[game]} ${text.unit} · ${zh ? "不赶时间，轻轻移动就好" : "Take your time. Move gently"}`;
  return <GentleGameArena runtime={controlsRuntime} progress={state.done / DISCOVERY_TOTALS[game]} arenaLabel={text.title} playingTitle={title} playingDetail={detail} className={`discovery-${game}-game`} controls={{ soundEnabled: audio.enabled, toggleSound: audio.toggle, paused, togglePause }}>
    <DiscoveryWorld game={game} state={state} cursor={runtime.cursor} active={runtime.stage === "playing"} paused={paused} />
  </GentleGameArena>;
}

export function DiscoveryGamePage({ game, session }: { game: DiscoveryGameId; session: Session | null }) {
  return <PlusGameGate session={session}><DiscoveryGame game={game} session={session} /></PlusGameGate>;
}
